import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
    PiArrowSquareOut,
    PiCalendarBlank,
    PiClipboardText,
    PiClock,
    PiCube,
    PiFileText,
    PiNote,
    PiPackage,
    PiTrashBold,
    PiWarning,
} from 'react-icons/pi';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import ShipmentTasksPanel from '@v2/features/shipment-tasks/ShipmentTasksPanel';
import ShipmentAiSummaryCard from '@v2/features/ai/ShipmentAiSummaryCard';
import type { ShipmentContainerRow, ShipmentTaskRow } from '@v2/features/shipment-tasks/types';
import type { ShipmentLinkedDeal } from '@v2/features/shipments/types';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import DataField from '@v2/features/ploomes-deals/detail/DataField';
import DetailCard from '@v2/features/ploomes-deals/detail/DetailCard';
import { formatBrl, formatDate, formatDateTime } from '@v2/features/ploomes-deals/utils';
import api from '@v2/lib/api';

type ShipmentEvent = {
    id: number;
    title: string;
    description?: string | null;
    event_type?: string | null;
    occurred_at: string | null;
    icon?: string | null;
    color?: string | null;
};

type ShipmentDetail = {
    id: number;
    order_number: string;
    process_code: string | null;
    process_name: string | null;
    client: string;
    stage: string | null;
    stage_label: string;
    process_status?: string | null;
    supplier_label?: string | null;
    invoice?: string | null;
    shipping_line?: string | null;
    vessel_name?: string | null;
    modality?: string | null;
    freight_type?: string | null;
    freight_value?: number | string | null;
    bl_number?: string | null;
    invoice_financial_date?: string | null;
    etd_origin?: string | null;
    eta_destination?: string | null;
    eta_actual?: string | null;
    order_date?: string | null;
    delay_risk_level?: string | null;
    delay_risk_score?: number | null;
    finished?: boolean | null;
    ploomes_deal_id?: number | null;
    linked_deals?: ShipmentLinkedDeal[];
    linked_deals_count?: number;
    clients_summary?: string[];
    events?: ShipmentEvent[];
    tasks?: ShipmentTaskRow[];
    containers_list?: ShipmentContainerRow[];
};

type ViewTab = 'tasks' | 'containers';

const RISK_CLASS: Record<string, string> = {
    baixo: 'bg-emerald-100 text-emerald-700',
    medio: 'bg-amber-100 text-amber-700',
    alto: 'bg-red-100 text-red-700',
};

function riskClass(level?: string | null): string {
    if (!level) return 'bg-gray-100 text-gray-600';
    const k = level
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    return RISK_CLASS[k] ?? 'bg-gray-100 text-gray-600';
}

function statusClass(status?: string | null): string {
    if (!status) return 'bg-gray-100 text-gray-600';
    if (status.toLowerCase() === 'finalizado') return 'bg-emerald-100 text-emerald-700';
    return 'bg-sky-100 text-sky-700';
}

export default function ShipmentDetailPage() {
    const { id } = useParams<{ id: string }>();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [tab, setTab] = useState<ViewTab>('tasks');
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const deleteMutation = useMutation({
        mutationFn: () => api.delete(`/shipments/${id}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['shipments'] });
            navigate('/shipments');
        },
    });

    function handleDeleteConfirm() {
        deleteMutation.mutate();
    }

    const { data: shipment, isLoading } = useQuery({
        queryKey: ['shipment', id],
        enabled: Boolean(id),
        queryFn: async () => {
            const { data } = await api.get<{ data: ShipmentDetail }>(`/shipments/${id}`);
            return data.data;
        },
    });

    const { data: links } = useQuery({
        queryKey: ['shipment-links', id],
        enabled: Boolean(id),
        queryFn: async () => {
            const { data } = await api.get(`/shipments/${id}/operational-links`);
            return data;
        },
    });

    // Stage color map — atualizado independentemente do cache de tasks
    const { data: stageSettingsData } = useQuery<{ stage: string; row_color: string | null }[]>({
        queryKey: ['stage-settings'],
        queryFn: async () => {
            const { data } = await api.get<{ data: { stage: string; row_color: string | null }[] }>('/stage-settings');
            return data.data;
        },
        staleTime: 60_000,
    });
    const stageColorMap = useMemo(() => {
        const map: Record<string, string | null> = {};
        for (const s of stageSettingsData ?? []) map[s.stage] = s.row_color;
        return map;
    }, [stageSettingsData]);

    const events = useMemo(() => shipment?.events ?? [], [shipment]);

    // Injeta stage_row_color nas tasks do cliente, sem depender de refetch do servidor
    const tasks = useMemo(
        () => (shipment?.tasks ?? []).map((t) => ({
            ...t,
            stage_row_color: t.stage ? (stageColorMap[t.stage] ?? null) : null,
        })),
        [shipment?.tasks, stageColorMap],
    );

    const containers = shipment?.containers_list ?? [];

    if (isLoading || !shipment) {
        return <LoadingState label="Carregando embarque…" />;
    }

    const freightValueNum =
        typeof shipment.freight_value === 'string' ? Number(shipment.freight_value) : shipment.freight_value ?? null;

    const shipmentTitle =
        shipment.process_code || shipment.process_name || shipment.order_number || `#${shipment.id}`;

    return (
        <div className="space-y-6">
            <ConfirmModal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                title="Apagar embarque"
                description={`Tem certeza que deseja apagar o embarque "${shipmentTitle}"? Esta ação não pode ser desfeita.`}
                confirmLabel="Sim, apagar"
                cancelLabel="Cancelar"
                loading={deleteMutation.isPending}
            />
            <PageHeader
                title={shipmentTitle}
                subtitle={shipment.process_name && shipment.process_code ? shipment.process_name : undefined}
                breadcrumb={[
                    { name: 'Dashboard', href: '/' },
                    { name: 'Embarques', href: '/shipments' },
                    { name: shipmentTitle },
                ]}
            >
                {shipment.process_code && shipment.order_number && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 dark:bg-zinc-800 dark:text-zinc-300">
                        Pedido {shipment.order_number}
                    </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                    <PiPackage className="h-3.5 w-3.5" />
                    {shipment.stage_label}
                </span>
                {shipment.process_status && (
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass(shipment.process_status)}`}>
                        {shipment.process_status}
                    </span>
                )}
                {shipment.delay_risk_level && (
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${riskClass(shipment.delay_risk_level)}`}
                    >
                        <PiWarning className="h-3.5 w-3.5" />
                        Risco {shipment.delay_risk_level}
                        {typeof shipment.delay_risk_score === 'number' && ` · ${shipment.delay_risk_score}`}
                    </span>
                )}
                {shipment.ploomes_deal_id && (
                    <Link to={`/ploomes-deals/${shipment.ploomes_deal_id}`}>
                        <Button variant="outline" size="sm" as="span">
                            <PiArrowSquareOut className="me-1.5 h-4 w-4" />
                            Proposta Ploomes
                        </Button>
                    </Link>
                )}
                <Link to={`/shipments/${id}/edit`}>
                    <Button variant="outline" size="sm" as="span">
                        Editar
                    </Button>
                </Link>
                {links?.v2?.receive_cd && (
                    <a href={links.v2.receive_cd}>
                        <Button variant="outline" size="sm" as="span">
                            Receive CD
                        </Button>
                    </a>
                )}
                {links?.v2?.check && (
                    <a href={links.v2.check}>
                        <Button variant="outline" size="sm" as="span">
                            Conferência
                        </Button>
                    </a>
                )}
                {links?.v2?.purchase_order && (
                    <a href={links.v2.purchase_order}>
                        <Button variant="outline" size="sm" as="span">
                            PO
                        </Button>
                    </a>
                )}
                <Link to={`/shipments/${id}/pi-check`}>
                    <Button variant="outline" size="sm" as="span">
                        Conferência PI
                    </Button>
                </Link>
                {links?.v2?.documents && (
                    <a href={links.v2.documents}>
                        <Button variant="outline" size="sm" as="span">
                            Documentos
                        </Button>
                    </a>
                )}
                <button
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:border-red-400 hover:bg-red-50"
                    title="Apagar embarque"
                >
                    <PiTrashBold className="h-3.5 w-3.5" />
                    Apagar
                </button>
            </PageHeader>

            <div className="-mt-4 flex flex-col gap-3">
                {(shipment.clients_summary?.length ? shipment.clients_summary : shipment.client ? [shipment.client] : []).length >
                    0 && (
                    <p className="text-sm text-gray-500 dark:text-zinc-400">
                        {(shipment.clients_summary?.length
                            ? shipment.clients_summary
                            : [shipment.client]
                        ).join(' · ')}
                    </p>
                )}

                {(shipment.linked_deals?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                            Pedidos
                        </span>
                        {shipment.linked_deals!.map((deal) => (
                            <Link
                                key={deal.id}
                                to={ploomesDealPath(deal)}
                                className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 transition hover:border-amber-300 hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300 dark:hover:bg-amber-500/25"
                                title={deal.label}
                            >
                                {deal.label}
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            <div className="grid gap-6 lg:grid-cols-12">
                <div className="space-y-6 lg:col-span-8">
                    <DetailCard title="Embarque" icon={<PiFileText className="h-5 w-5" />} className="h-auto">
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                            <DataField label="Fornecedor" value={shipment.supplier_label} tone="primary" />
                            <DataField label="Invoice" value={shipment.invoice} tone="info" />
                            <DataField label="BL" value={shipment.bl_number} tone="info" />
                            <DataField label="Modalidade" value={shipment.modality} tone="gray" />
                            <DataField label="Tipo de Frete" value={shipment.freight_type} tone="gray" />
                            <DataField
                                label="Valor do Frete"
                                value={
                                    typeof freightValueNum === 'number' && !Number.isNaN(freightValueNum)
                                        ? formatBrl(freightValueNum)
                                        : null
                                }
                                tone="success"
                            />
                            <DataField label="Armador" value={shipment.shipping_line} tone="gray" />
                            <DataField label="Navio" value={shipment.vessel_name} tone="gray" />
                            <DataField label="Cliente" value={shipment.client} tone="primary" />
                        </div>
                    </DetailCard>

                    <DetailCard title="Datas e Operação" icon={<PiCalendarBlank className="h-5 w-5" />} className="h-auto">
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                            <DataField label="Data do Pedido" value={formatDate(shipment.order_date)} tone="gray" />
                            <DataField
                                label="Pagamento Financeiro"
                                value={formatDate(shipment.invoice_financial_date)}
                                tone="success"
                            />
                            <DataField label="ETD Origem" value={formatDate(shipment.etd_origin)} tone="info" />
                            <DataField label="ETA Destino" value={formatDate(shipment.eta_destination)} tone="info" />
                            <DataField label="ETA Real" value={formatDate(shipment.eta_actual)} tone="warning" />
                            <DataField
                                label="Score de Risco"
                                value={
                                    typeof shipment.delay_risk_score === 'number'
                                        ? `${shipment.delay_risk_score} / 100`
                                        : null
                                }
                                tone="warning"
                            />
                        </div>
                    </DetailCard>

                    <DetailCard
                        className="h-auto"
                        title={
                            <span className="inline-flex flex-wrap items-center gap-2">
                                Tarefas e Containers
                                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                                    {tasks.length} tarefas
                                </span>
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                                    {containers.length} containers
                                </span>
                            </span>
                        }
                        icon={<PiClipboardText className="h-5 w-5" />}
                    >
                        <div className="mb-4 -mx-1 flex items-center gap-2 overflow-x-auto whitespace-nowrap border-b border-gray-100 px-1 pb-3 [scrollbar-width:thin]">
                            <button
                                type="button"
                                onClick={() => setTab('tasks')}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition ${
                                    tab === 'tasks' ? 'bg-orange-100 text-orange-700' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                <PiClipboardText className="h-4 w-4" />
                                Tarefas
                                <span
                                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                                        tab === 'tasks' ? 'bg-orange-200 text-orange-800' : 'bg-gray-200 text-gray-600'
                                    }`}
                                >
                                    {tasks.length}
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setTab('containers')}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition ${
                                    tab === 'containers' ? 'bg-orange-100 text-orange-700' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                <PiCube className="h-4 w-4" />
                                Containers
                                <span
                                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                                        tab === 'containers' ? 'bg-orange-200 text-orange-800' : 'bg-gray-200 text-gray-600'
                                    }`}
                                >
                                    {containers.length}
                                </span>
                            </button>
                        </div>
                        <ShipmentTasksPanel
                            shipmentId={shipment.id}
                            tasks={tasks}
                            containers={containers}
                            onRefresh={() => {
                                qc.invalidateQueries({ queryKey: ['shipment', id] });
                                // Invalida a lista para atualizar cor/estágio
                                qc.invalidateQueries({ queryKey: ['shipments'] });
                            }}
                            view={tab}
                            hideToggle
                        />
                    </DetailCard>
                </div>

                <div className="space-y-6 lg:col-span-4">
                    <ShipmentAiSummaryCard shipmentId={shipment.id} />
                    <DetailCard title="Timeline" icon={<PiClock className="h-5 w-5" />}>
                        {events.length === 0 ? (
                            <p className="py-4 text-sm text-gray-500">Nenhum evento registrado.</p>
                        ) : (
                            <ol className="relative space-y-5 ps-1">
                                {events.map((e, idx) => {
                                    const isLast = idx === events.length - 1;
                                    const color = e.color || '#f97316';
                                    return (
                                        <li key={e.id} className="relative flex gap-3">
                                            {!isLast && (
                                                <span
                                                    aria-hidden
                                                    className="absolute start-[0.7rem] top-6 h-[calc(100%+0.25rem)] w-px bg-gray-200"
                                                />
                                            )}
                                            <span
                                                className="relative z-10 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white shadow-sm ring-2 ring-white"
                                                style={{ backgroundColor: color }}
                                            >
                                                <PiNote className="h-3.5 w-3.5" />
                                            </span>
                                            <div className="min-w-0 flex-1 pb-1">
                                                <p className="text-sm font-semibold text-gray-900">{e.title}</p>
                                                <p className="text-xs text-gray-500">{formatDateTime(e.occurred_at)}</p>
                                                {e.description && (
                                                    <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600">
                                                        {e.description}
                                                    </p>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </DetailCard>
                </div>
            </div>
        </div>
    );
}
